import { useQuery, UseQueryResult } from "@tanstack/react-query"
import { useIntl } from "react-intl"
import { carrotClient } from "#/utils/CarrotClient"
import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"
import { getHeap } from "#/utils/heap"
import ResponseError from "#/types/responseError"
import type {
  HelpCenterArticleDetail,
  HelpCenterArticleResponse,
  HelpCenterCategory,
  HelpCenterResponse
} from "./types"
import { useHelpCenterPreview, type HelpCenterPreviewState } from "./helpCenterPreviewStore"

const MEMBER_VIEW_STALE_TIME_MS = 1000 * 60 * 5

async function loadHelpCenter(locale: string): Promise<HelpCenterResponse> {
  const beforeFetch = performance.now()
  const response = await carrotClient.getHelpCenter(locale)
  const afterFetch = performance.now()

  getHeap().track("Help Center Tree Load Time", {
    "Help Center Tree Load Time In Milliseconds": afterFetch - beforeFetch
  })

  return response
}

function helpCenterTreeQueryKey(locale: string, preview: HelpCenterPreviewState): string[] {
  return preview.active
    ? ["help-center", "preview", locale, JSON.stringify(preview.memberContextOverrides ?? null)]
    : ["help-center", locale]
}

function helpCenterArticleQueryKey(id: string, locale: string, preview: HelpCenterPreviewState): string[] {
  return preview.active
    ? ["help-center-article", "preview", id, locale, JSON.stringify(preview.memberContextOverrides ?? null)]
    : ["help-center-article", id, locale]
}

function useGetHelpCenter<TData = HelpCenterResponse>(
  select?: (data: HelpCenterResponse) => TData,
  enabled = true
): UseQueryResult<TData, ResponseError> {
  const { locale } = useIntl()
  const preview = useHelpCenterPreview()
  return useQuery<HelpCenterResponse, ResponseError, TData>({
    queryKey: helpCenterTreeQueryKey(locale, preview),
    queryFn: () =>
      preview.active
        ? carrotClient.getHelpCenterPreview(locale, preview.memberContextOverrides ?? undefined)
        : loadHelpCenter(locale),
    enabled,
    refetchOnWindowFocus: false,
    retry: buildCarrotApiRetryPolicy(3),
    staleTime: preview.active ? 0 : MEMBER_VIEW_STALE_TIME_MS,
    select
  })
}

export function useHelpCenterCategories(): UseQueryResult<HelpCenterCategory[], ResponseError> {
  return useGetHelpCenter(({ categories }) => sortCategories(categories))
}

export function useHelpCenterCategoryBySlug(
  slug: string
): UseQueryResult<HelpCenterCategory | undefined, ResponseError> {
  return useGetHelpCenter(({ categories }) => categories.find((category) => category.slug === slug), slug.length > 0)
}

function sortCategories(categories: HelpCenterCategory[]): HelpCenterCategory[] {
  return [...categories].sort((a, b) => {
    const aOrder = a.order ?? Number.POSITIVE_INFINITY
    const bOrder = b.order ?? Number.POSITIVE_INFINITY
    if (aOrder !== bOrder) return aOrder - bOrder
    return a.title.localeCompare(b.title)
  })
}

export function useHelpCenterArticle(id: string): UseQueryResult<HelpCenterArticleDetail, ResponseError> {
  const { locale } = useIntl()
  const preview = useHelpCenterPreview()
  return useQuery<HelpCenterArticleResponse, ResponseError, HelpCenterArticleDetail>({
    queryKey: helpCenterArticleQueryKey(id, locale, preview),
    queryFn: () =>
      preview.active
        ? carrotClient.getHelpCenterArticlePreview(id, locale, preview.memberContextOverrides ?? undefined)
        : carrotClient.getHelpCenterArticle(id, locale),
    enabled: id.length > 0,
    refetchOnWindowFocus: false,
    retry: buildCarrotApiRetryPolicy(3),
    staleTime: preview.active ? 0 : MEMBER_VIEW_STALE_TIME_MS,
    select: ({ article }) => article
  })
}
